/**
 * Flow F's calls. Every one goes through `../../api/client.js` and nothing else.
 *
 * There is no `data.ts` behind F1–F6 any more. Every Bangla string on these six screens
 * arrives from the Worker already localised, including the two on F1 that are FACTS
 * rather than labels — `২টি` beside ঠিকানা and `বাংলা` beside ভাষা ও দেশ. Both are
 * computed server-side from the same read that produces the rest of the account, so the
 * account home cannot claim two addresses while the address screen shows three.
 *
 * ── EVERY WRITE ANSWERS WITH THE WHOLE SCREEN ───────────────────────────────
 * Not with an ack. A screen that patches its own local copy after a successful write is
 * a screen that will eventually disagree with the server about what was saved — and on
 * an address book or a card list, that disagreement is what a customer finds out about
 * at checkout. Re-rendering from the answer costs one payload and removes the class.
 */
import type {
  AccountOverview,
  AddressesResponse,
  CreateAddressRequest,
  LanguageSettings,
  PaymentMethodsResponse,
  AddPaymentCardRequest,
  SecurityResponse,
  UpdateAddressRequest,
  UpdateLanguageRequest,
  UpdateProfileRequest,
  UpdateSecurityToggleRequest,
} from '@oprappo/contracts';
import { api } from '../../api/client.js';

/** F1, F2 and F5 — one read. See the file header for why. */
export function fetchAccount(signal?: AbortSignal): Promise<AccountOverview> {
  return api.get<AccountOverview>('/v1/account/profile', { signal });
}

export function saveProfile(patch: UpdateProfileRequest): Promise<AccountOverview> {
  return api.patch<AccountOverview>('/v1/account/profile', patch);
}

export function setLanguage(body: UpdateLanguageRequest): Promise<LanguageSettings> {
  return api.patch<LanguageSettings>('/v1/account/language', body);
}

/**
 * F2's `অ্যাকাউন্ট বন্ধ করতে চাই`, and the way back from it.
 *
 * The Privacy Policy, the Terms of Service and F2 itself all promise that an account can
 * be closed and the data deleted thirty days later. Two of the three are binding. Until
 * this wave the button had no handler, there was no route and there was no table — a
 * promise readable on three surfaces and invocable on none.
 *
 * Both answer with the whole account, like every other write on this surface, so the block
 * that draws the button and the block that reports the request cannot disagree about which
 * of them belongs on screen. No idempotency key: the request is an upsert on one row per
 * identity, so a double tap is one clock, and a deliberate SECOND ask after a cancellation
 * must reset the window rather than replay the first answer.
 */
export function requestAccountClosure(): Promise<AccountOverview> {
  return api.post<AccountOverview>('/v1/account/closure', {});
}

export function cancelAccountClosure(): Promise<AccountOverview> {
  return api.delete<AccountOverview>('/v1/account/closure');
}

/* -------------------------------------------------------------------- F3 --- */

export function fetchAddresses(signal?: AbortSignal): Promise<AddressesResponse> {
  return api.get<AddressesResponse>('/v1/account/addresses', { signal });
}

export function addAddress(body: CreateAddressRequest): Promise<AddressesResponse> {
  return api.post<AddressesResponse>('/v1/account/addresses', body);
}

export function updateAddress(body: UpdateAddressRequest): Promise<AddressesResponse> {
  return api.patch<AddressesResponse>('/v1/account/addresses', body);
}

export function removeAddress(id: string): Promise<AddressesResponse> {
  return api.delete<AddressesResponse>(`/v1/account/addresses?id=${encodeURIComponent(id)}`);
}

/* -------------------------------------------------------------------- F4 --- */

export function fetchPaymentMethods(signal?: AbortSignal): Promise<PaymentMethodsResponse> {
  return api.get<PaymentMethodsResponse>('/v1/account/payment-methods', { signal });
}

/**
 * Registering a card.
 *
 * `AddPaymentCardRequest` has four fields and there is no fifth. A card number cannot be
 * sent from here because there is nowhere to put one, the route refuses unknown fields,
 * and the table has no column — three independent refusals, which is what it takes for
 * "no PAN, ever" to be a property of the system rather than a promise in a comment.
 */
export function addPaymentCard(body: AddPaymentCardRequest): Promise<PaymentMethodsResponse> {
  return api.post<PaymentMethodsResponse>('/v1/account/payment-methods', body);
}

export function removePaymentCard(id: string): Promise<PaymentMethodsResponse> {
  return api.delete<PaymentMethodsResponse>(
    `/v1/account/payment-methods?id=${encodeURIComponent(id)}`,
  );
}

/* -------------------------------------------------------------------- F6 --- */

export function fetchSecurity(signal?: AbortSignal): Promise<SecurityResponse> {
  return api.get<SecurityResponse>('/v1/account/security', { signal });
}

export function setSecurityToggle(body: UpdateSecurityToggleRequest): Promise<SecurityResponse> {
  return api.patch<SecurityResponse>('/v1/account/security', body);
}

/** F6's drawn `বের করে দিন`. The current device is refused by the route, not by the screen. */
export function removeDevice(id: string): Promise<SecurityResponse> {
  return api.delete<SecurityResponse>(
    `/v1/account/security/sessions?id=${encodeURIComponent(id)}`,
  );
}
